import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { safeStorage } from '../utils/safeStorage';

/**
 * ThemeContext
 * ---------------------------------------------------------------
 * Three modes: `'light' | 'night' | 'system'`.
 *   - `light` / `night`   — user-forced, persisted via safeStorage.
 *   - `system`            — follows `prefers-color-scheme`; persists the
 *                            preference, not the resolved value.
 *
 * `theme` (back-compat) remains the *resolved* value (`'light'` | `'night'`)
 * so every existing call site (`useTheme().theme`, `.isNight`,
 * `toggleTheme`) keeps working.  `themePreference` exposes the raw user
 * choice for Settings UIs that need to show the three-way picker.
 */

const ThemeContext = createContext(null);
const STORAGE_KEY = 'polkdesq_theme';
const VALID_PREFS = new Set(['light', 'night', 'system']);

function readInitialPreference() {
  const stored = safeStorage.getString(STORAGE_KEY);
  if (stored && VALID_PREFS.has(stored)) return stored;
  // Light is the new default — feedback (Feb 2026): users want a brighter
  // first impression. Dark remains a first-class mode, just opt-in via the
  // theme toggle in the topbar.
  return 'light';
}

function resolveTheme(preference) {
  if (preference !== 'system') return preference;
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'night' : 'light';
  } catch {
    return 'light';
  }
}

export function ThemeProvider({ children }) {
  const [themePreference, setThemePreference] = useState(readInitialPreference);
  const [theme, setResolvedTheme] = useState(() => resolveTheme(readInitialPreference()));

  // Persist preference + apply resolved theme. When in `system` mode, subscribe
  // to OS-level changes. A single effect handles everything — avoids the
  // earlier double-write of `data-theme` on mount.
  useEffect(() => {
    safeStorage.setString(STORAGE_KEY, themePreference);
    const apply = (next) => {
      setResolvedTheme(next);
      document.documentElement.setAttribute('data-theme', next);
    };
    apply(resolveTheme(themePreference));

    if (themePreference !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => apply(mq.matches ? 'night' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [themePreference]);

  const setTheme = useCallback((next) => {
    // Back-compat: historically setTheme accepted 'light' | 'night' only.
    // We now also accept 'system'.
    if (VALID_PREFS.has(next)) setThemePreference(next);
  }, []);

  const toggleTheme = useCallback(() => {
    // Toggle only flips between light ↔ night, matching the existing icon
    // button in the top-bar.  If currently 'system', we snap to the
    // opposite of whatever the system resolved to.
    setThemePreference((prev) => {
      const current = resolveTheme(prev);
      return current === 'light' ? 'night' : 'light';
    });
  }, []);

  const value = useMemo(() => ({
    theme,                 // resolved: 'light' | 'night'
    themePreference,       // raw user choice: 'light' | 'night' | 'system'
    setTheme,              // accepts 'light' | 'night' | 'system'
    setThemePreference: setTheme, // alias
    toggleTheme,
    isNight: theme === 'night',
    isSystem: themePreference === 'system',
  }), [theme, themePreference, setTheme, toggleTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
}
